import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import api, { saveAuth } from "@/lib/api";
import Nav from "@/components/Nav";
import { ArrowRight, Brain, HeartHandshake, Gauge, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const startAsGuest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/guest", { display_name: "Guest" });
      saveAuth(data);
      const start = await api.post("/assessment/start");
      nav(`/assessment/${start.data.assessment_id}`);
    } catch (e) { toast.error("Could not start. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#38BDF8]/10 blur-[120px] glow-pulse pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#F59E0B]/10 blur-[120px] glow-pulse pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 pt-14 pb-24 relative">
          <div className="uppercase tracking-[0.24em] text-xs font-mono text-[#E0A96D] mb-6 fade-up" data-testid="hero-eyebrow">Two Sides of Intelligence · One Complete Picture</div>
          <h1 className="font-serif font-medium text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl fade-up" data-testid="hero-headline">
            How does your <span className="gold-underline">intelligence</span> actually <em className="text-[#E0A96D] not-italic">work?</em>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-400 text-base sm:text-lg leading-relaxed fade-up" data-testid="hero-sub">
            DualQ examines cognitive reasoning, emotional intelligence, decision-making and confidence calibration — in one assessment.
            Free to take. Unlock your complete personalized profile for <span className="text-slate-100 font-medium">$1.99</span>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up">
            <button
              onClick={startAsGuest}
              disabled={loading}
              className="group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] transition-colors"
              data-testid="hero-start-btn"
            >
              {loading ? "Starting…" : "Take the free assessment"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <Link to="/auth" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-slate-700 hover:border-[#E0A96D]/40 transition-colors text-slate-200" data-testid="hero-signin-btn">
              I already have an account
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500 font-mono" data-testid="hero-fineprint">One-time payment. No subscription required. Your result stays unlocked.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-16 grid gap-4 sm:grid-cols-3">
        {[
          {icon: Brain, title:"Cognitive Reasoning", body:"Logic, patterns, spatial, memory, quantitative and flexibility across adaptive items.", color:"#38BDF8"},
          {icon: HeartHandshake, title:"Emotional / Social", body:"Perspective, communication, conflict, empathy — measured through scenarios, not just self-report.", color:"#F59E0B"},
          {icon: Gauge, title:"Metacognitive Calibration", body:"How well your confidence matches your accuracy — the third dimension most tests miss.", color:"#A855F7"},
        ].map(({icon:Icon,title,body,color}) => (
          <div key={title} className="rounded-2xl bg-[#131E36] border border-[#E0A96D]/10 p-6 hover:border-[#E0A96D]/30 transition-colors" data-testid={`feature-${title.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="w-11 h-11 rounded-lg grid place-items-center mb-4" style={{background: color+"22", color}}>
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-xl mb-2">{title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-24">
        <div className="rounded-3xl border border-slate-800/70 bg-gradient-to-br from-[#0E172A] to-[#131E36] p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-8" data-testid="cta-band">
          <div className="flex-1">
            <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">The DualQ Difference</div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-3">Insight, not judgment. Development, not ranking.</h2>
            <p className="text-slate-400 max-w-xl">All items in this MVP are marked EXPERIMENTAL. DualQ is designed to earn scientific credibility over time through formal validation — not by copying legacy tests.</p>
          </div>
          <button
            onClick={startAsGuest}
            className="inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-white text-[#070C18] font-semibold hover:bg-slate-200 transition-colors"
            data-testid="cta-start"
          >
            <Sparkles className="w-4 h-4" /> Start now
          </button>
        </div>
      </section>
    </div>
  );
}
